import "./JobBoardComponent.scss";
import JobHeaderComponent from "./job-header/JobHeaderComponent";
import JobFilterComponent from "./job-filter/JobFilterComponent";
import {useCallback, useEffect, useMemo, useState, useRef} from "react";
import JobCardComponent from "./job-card/JobCardComponent";
import InfiniteScroll from "react-infinite-scroll-component";
import {Skeleton} from "antd";
import {CommonService} from "../../helpers";
import {ENV} from "../../constants";
import LoaderComponent from "../../components/loader/LoaderComponent";
import JobDetailsComponent from "../job-details/JobDetailsComponent";
import NoJobSelectedComponent from "../../components/no-job-selected/NoJobSelectedComponent";
import { useNavigate, useParams,useSearchParams } from "react-router-dom";
import NoJobsFoundComponent from "../../components/no-job-found/NoJobFoundComponent";
import {
    searchParamsToFilterData,
    filterDataToSearchParams,
    buildApiListPayload,
    buildCountsFilterPayload,
    getCountsFilterKey,
    mapJobTypeCountsFromApi,
    JobTypeCountsMap,
} from "./jobBoardSearchParams";
import { useDispatch, useSelector } from "react-redux";
import { StateParams } from "../../store/reducers";
import { openSignUpModal } from "../../store/actions/meta.action";

const JobBoardComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state: StateParams) => state.auth);
    const { id: urlJobId } = useParams();
    const isInitialMount = useRef(true);
    const jobViewCountRef = useRef(0);
    const hasShownResumePromptRef = useRef(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const filterData = useMemo(() => searchParamsToFilterData(searchParams), [searchParams]);
    const filterStateFromUrl = useMemo(() => ({
        selectedProfessions: filterData.staff_types || [],
        selectedSpeciality: filterData.specialities || [],
        selectedStates: filterData.states || [],
        citySearch: filterData.search || '',
        startDate: filterData.start_date ? new Date(filterData.start_date) : null,
        endDate: filterData.end_date ? new Date(filterData.end_date) : null,
        minSalary: filterData.min_salary ?? null,
        maxSalary: filterData.max_salary ?? null,
        hotJobFilter: filterData.is_hot_job ? 'hot' : 'all',
    }), [filterData]);
    const [jobList, setJobList] = useState<any>([]);
    const [isJobListLoading, setIsJobListLoading] = useState(false);
    const [totalJobCount, setTotalJobCount] = useState<number>(0);
    const [hasMore, setHasMore] = useState(true);
    const [selectedJobId, setSelectedJobId] = useState<any>(urlJobId || null);
    const [jobDetails, setJobDetails] = useState<any>(null);
    const [isJobDetailsLoading, setIsJobDetailsLoading] = useState<boolean>(false);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
    const [showMobileDetails, setShowMobileDetails] = useState(!!urlJobId);
    const hasHandledUrlJobRef = useRef<boolean>(false);
    const [jobTypeCounts, setJobTypeCounts] = useState<JobTypeCountsMap>({});

    const countsFilterKey = useMemo(() => getCountsFilterKey(filterData), [filterData]);

    const fetchJobTypeCounts = useCallback((baseFilters: ReturnType<typeof buildCountsFilterPayload>) => {
        CommonService._api
            .post(ENV.API_URL + 'ats/websiteJobsJobTypeCounts', baseFilters)
            .then((resp) => {
                setJobTypeCounts(mapJobTypeCountsFromApi(resp?.data));
            })
            .catch(() => {});
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const updateURL = useCallback((jobType: string, jobId?: string) => {
        const newSearchParams = new URLSearchParams(searchParams);

        // Always add job_type to URL
        newSearchParams.set('type', jobType);

        if (jobId) {
            navigate(`/job/${jobId}?${newSearchParams.toString()}`, { replace: true });
        } else {
            navigate(`/job?${newSearchParams.toString()}`, { replace: true });
        }
    }, [navigate, searchParams]);

    // Helper function to deduplicate jobs by _id
    const deduplicateJobs = useCallback((jobs: any[]) => {
        const seen = new Set();
        return jobs.filter((job) => {
            if (seen.has(job._id)) {
                return false;
            }
            seen.add(job._id);
            return true;
        });
    }, []);

    const handleUrlJobIfNeeded = useCallback((currentJobList: any) => {
        // Check if we need to handle URL job
        if (urlJobId && urlJobId === selectedJobId && !hasHandledUrlJobRef.current) {
            const jobExistsInList = currentJobList.some((job: any) => job._id === urlJobId);

            if (!jobExistsInList) {
                // Fetch the URL job details and add it to the list
                CommonService._api.get(ENV.API_URL + `ats/${urlJobId}/websiteJobView`).then((response) => {
                    const urlJobData = response?.data;
                    if (urlJobData) {
                        setJobList((currentList: any) => {
                            const updatedList = [urlJobData, ...currentList];
                            const dedupedList = deduplicateJobs(updatedList);
                            return dedupedList;
                        });
                        setJobDetails(urlJobData);

                        // Scroll to the job
                        setTimeout(() => {
                            const jobCard = document.querySelector(`[data-job-id="${urlJobId}"]`);
                            if (jobCard) {
                                jobCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }
                        }, 100);

                        // Mark as handled
                        hasHandledUrlJobRef.current = true;
                    }
                }).catch((error) => {
                    console.log('Error fetching URL job:', error);
                });
            } else {
                // Job exists in list, just scroll to it
                setTimeout(() => {
                    const jobCard = document.querySelector(`[data-job-id="${urlJobId}"]`);
                    if (jobCard) {
                        jobCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }, 100);
                hasHandledUrlJobRef.current = true;
            }
        }
    }, [urlJobId, selectedJobId, deduplicateJobs]);

    const getListData = useCallback(() => {
        setIsJobListLoading(true);
        console.log('API Call with filterData:', filterData);

        const apiPayload = buildApiListPayload(filterData);

        CommonService._api.post(ENV.API_URL + `ats/websiteJobsList`, apiPayload).then((resp) => {
            const newJobList = resp?.data?.docs || [];

            setJobList((prevJobList: any) => {
                const combinedList = filterData.page === 1 ? newJobList : [...prevJobList, ...newJobList];
                // Deduplicate jobs
                const uniqueList = deduplicateJobs(combinedList);

                // Handle URL job with the current list
                handleUrlJobIfNeeded(uniqueList);

                return uniqueList;
            });

            setTotalJobCount(resp?.data?.total || 0);

            // Determine hasMore from the API's authoritative pagination metadata
            // (response: { page, pages, total, limit, docs }). Falling back to a
            // page-size heuristic only if the metadata is missing.
            const totalPages = resp?.data?.pages;
            const currentPage = resp?.data?.page ?? filterData.page;
            if (typeof totalPages === 'number') {
                setHasMore(currentPage < totalPages);
            } else {
                setHasMore(newJobList.length > 0);
            }

            setIsJobListLoading(false);
        }).catch((err) => {
            console.log(err);
            setHasMore(false); // Also set hasMore to false on error
            setIsJobListLoading(false);
        });
    }, [filterData, deduplicateJobs, handleUrlJobIfNeeded]);

    // Job type counts: initial load and when filters change (not on tab click or pagination)
    useEffect(() => {
        fetchJobTypeCounts(JSON.parse(countsFilterKey));
    }, [countsFilterKey, fetchJobTypeCounts]);

    // Effect to handle filter changes and initial load
    useEffect(() => {
        // Clear job details when filters change (except for page changes)
        const isPageChange = filterData.page > 1;

        if (!isPageChange && !isInitialMount.current) {
            // Only clear job details if there's no URL job ID (direct navigation to job board)
            if (!urlJobId) {
                setJobDetails(null);
                setSelectedJobId(null);
                setShowMobileDetails(false);
                setHasMore(true);
            }
        }

        getListData();

        if (isInitialMount.current) {
            isInitialMount.current = false;
        }
    }, [filterData, getListData, urlJobId]);

    const loadMoreJobs = useCallback(() => {
        if (hasMore && !isJobListLoading) {
            const params = filterDataToSearchParams({ ...filterData, page: filterData.page + 1 });
            // replace (not push) so pagination doesn't pollute the browser history stack
            setSearchParams(params, { replace: true });
        }
    }, [hasMore, isJobListLoading, filterData, setSearchParams]);

    const getJobDetails = useCallback((jobId: any) => {
        if (!jobId) return;

        setIsJobDetailsLoading(true);
        CommonService._api.get(ENV.API_URL + `ats/${jobId}/websiteJobView`).then((response) => {
            setJobDetails(response?.data);
        }).catch((error: any) => {
            console.log(error.error);
            // If job details fail to load, remove from URL
            if (urlJobId === jobId) {
                updateURL(filterData.job_type);
            }
        }).finally(() => {
            setIsJobDetailsLoading(false);
        });
    }, [urlJobId, updateURL, filterData]);

    const handleCardClick = useCallback((jobId: any) => {
        setSelectedJobId(jobId);
        getJobDetails(jobId);
        // Update URL with job ID
        updateURL(filterData.job_type, jobId)
        if (isMobileView) {
            setShowMobileDetails(true);
        }
        // Show resume prompt after 3 job views for unauthenticated users
        if (!user && !hasShownResumePromptRef.current) {
            jobViewCountRef.current += 1;
            if (jobViewCountRef.current >= 3) {
                hasShownResumePromptRef.current = true;
                dispatch(openSignUpModal());
            }
        }
    }, [getJobDetails, isMobileView, updateURL, filterData?.job_type, user, dispatch]);

    const handleBackToList = () => {
        setShowMobileDetails(false);
        setSelectedJobId(null);
        setJobDetails(null);
        // Remove job ID from URL
        updateURL(filterData.job_type);
    };

    // Enhanced filter update function — persist to URL (single source of truth)
    const updateFilterData = useCallback((newFilterData: any) => {
        setIsJobListLoading(true);
        setJobDetails(null);
        setSelectedJobId(null);
        setJobList([]);
        const params = filterDataToSearchParams(newFilterData);
        setSearchParams(params);
        navigate(`/job?${params.toString()}`, { replace: true });
    }, [navigate, setSearchParams]);

    // If active tab is no longer available after counts refresh, fall back to first tab
    useEffect(() => {
        const availableTypes = Object.keys(jobTypeCounts);
        if (availableTypes.length === 0) return;
        if (jobTypeCounts[filterData.job_type] != null) return;

        const fallbackType = jobTypeCounts.all != null ? 'all' : availableTypes[0];
        if (fallbackType && fallbackType !== filterData.job_type) {
            updateFilterData({ ...filterData, job_type: fallbackType, page: 1 });
        }
    }, [jobTypeCounts, filterData, updateFilterData]);

    // Handle URL job ID changes - this handles both initial load and URL changes
    useEffect(() => {
        if (urlJobId) {
            // If we have a URL job ID and it's different from selected, or if we don't have job details
            if (urlJobId !== selectedJobId || !jobDetails) {
                setSelectedJobId(urlJobId);
                getJobDetails(urlJobId);
                if (isMobileView) {
                    setShowMobileDetails(true);
                }
            }
        } else if (!urlJobId && selectedJobId) {
            // If no URL job ID but we have a selected job, clear everything
            setSelectedJobId(null);
            setJobDetails(null);
            setShowMobileDetails(false);
        }
    }, [urlJobId, selectedJobId, jobDetails, isMobileView, getJobDetails]);

    return (
        <div className='job-board-component'>
            <div
                className={`job-board-header`}
            >
                <JobHeaderComponent />
            </div>
            <div
                className={`job-board-filter-wrapper`}
            >
                <JobFilterComponent
                    setFilterData={updateFilterData}
                    activeJobType={filterData?.job_type}
                    setJobList={setJobList}
                    filterStateFromUrl={filterStateFromUrl}
                    jobTypeCounts={jobTypeCounts}
                    onJobTypeChange={(type) => updateFilterData({ ...filterData, job_type: type, page: 1 })}
                />
            </div>
            <div className={`job-board-main`}>
                <div className={`job-list-wrapper ${showMobileDetails && isMobileView ? 'mobile-hidden' : ''}`}>
                    <div className='heading-text'>
                        Openings ({jobTypeCounts[filterData.job_type] ?? totalJobCount})
                    </div>
                    <div className='job-list-content-wrapper' id='job-card-wrapper'>
                        {jobList.length === 0 && !isJobListLoading ? (
                            <NoJobsFoundComponent />
                        ) : (
                            <InfiniteScroll
                                dataLength={jobList?.length}
                                next={loadMoreJobs}
                                hasMore={hasMore}
                                // Start fetching ~600px before the user reaches the bottom so the
                                // next page is already in place by the time they scroll to it.
                                scrollThreshold="600px"
                                loader={
                                    jobList.length === 0 ? (
                                        // Initial load: full skeleton cards
                                        Array.from({ length: 6 }).map((_, index) => (
                                            <div key={index} className='job-card-component'>
                                                <Skeleton active paragraph={{ rows: 5 }} />
                                            </div>
                                        ))
                                    ) : (
                                        // Pagination: lightweight inline spinner so the list
                                        // doesn't jump while the next page streams in
                                        <div className='job-list-loading-more'>
                                            <LoaderComponent type='spinner' size='xs' />
                                        </div>
                                    )
                                }
                                endMessage={
                                    <div className='no-more-jobs-message'>
                                        <p>No more jobs to load.</p>
                                    </div>
                                }
                                scrollableTarget="job-card-wrapper">
                                {jobList.map((job: any) => (
                                    <JobCardComponent
                                        key={job?._id}
                                        job_details={job}
                                        handleCardClick={handleCardClick}
                                        selectedJobId={selectedJobId}
                                    />
                                ))}
                            </InfiniteScroll>
                        )}
                    </div>
                </div>
                <div className={`job-details-wrapper ${showMobileDetails && isMobileView ? 'mobile-visible' : ''}`}>
                    {isMobileView && showMobileDetails && (
                        <button className='mobile-back-button' onClick={handleBackToList}>
                            ← Back to Jobs
                        </button>
                    )}
                    {isJobDetailsLoading ?
                        <div className='job-details-loading-wrapper'>
                            <LoaderComponent type='spinner' size={'sm'}/>
                        </div> :
                        <div>
                            {jobDetails ?
                                <JobDetailsComponent jobDetails={jobDetails} /> :
                                <NoJobSelectedComponent/>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default JobBoardComponent
